import { useState, useMemo, useCallback } from 'react';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableColumnHeader } from '@/components/insurance/SortableColumnHeader';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { useOrgContext } from '@/hooks/useOrgContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { DeletePoliciesDialog } from '@/components/insurance/DeletePoliciesDialog';
import { InvoiceStatusBadge } from '@/components/insurance/InvoiceStatusBadge';
import { ColumnSettingsPopover } from '@/components/insurance/ColumnSettingsPopover';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { INVOICES_COLUMNS } from '@/lib/tableColumns';
import { cn } from '@/lib/utils';
import type { IInvoiceListItem } from '@/@types/invoice';

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(amount: number, currency: string): string {
  return `${currency} ${amount.toFixed(2)}`;
}

function InvoiceTableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="flex items-center px-6 py-3 border-b border-table-row-border gap-4 min-w-max"
        >
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-36 ml-4" />
          <Skeleton className="h-4 w-24 ml-4" />
          <Skeleton className="h-4 w-20 ml-4" />
          <Skeleton className="h-4 w-24 ml-4" />
          <Skeleton className="h-4 w-24 ml-auto" />
        </div>
      ))}
    </>
  );
}

function renderInvoiceCell(colId: string, invoice: IInvoiceListItem): React.ReactNode {
  switch (colId) {
    case 'invoiceNumber':
      return (
        <span className="text-sm font-medium text-foreground truncate pr-3">
          {invoice.invoiceNumber}
        </span>
      );
    case 'company':
      return (
        <span className="text-sm text-muted-foreground truncate pr-3">{invoice.companyName}</span>
      );
    case 'total':
      return (
        <span className="text-sm text-foreground font-mono pr-3">
          {formatCurrency(invoice.total, invoice.currency)}
        </span>
      );
    case 'status':
      return <InvoiceStatusBadge status={invoice.status} />;
    case 'dueDate':
      return (
        <span className="text-sm text-muted-foreground">
          {invoice.dueDate ? formatDate(invoice.dueDate) : '—'}
        </span>
      );
    case 'created':
      return <span className="text-sm text-muted-foreground">{formatDate(invoice.createdAt)}</span>;
    default:
      return null;
  }
}

export function InvoicesListPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<Id<'companyInvoices'>>>(new Set());
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { visibleColumns, allColumns, isColumnVisible, toggleColumn, resetToDefaults } =
    useColumnVisibility('insurance-invoices', INVOICES_COLUMNS);

  const rawInvoices = useQuery(
    api.invoices.queries.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const batchRemove = useMutation(api.invoices.mutations.batchRemove);

  const isLoading = orgLoading || rawInvoices === undefined;
  const invoices = useMemo(
    () => (rawInvoices ?? []) as IInvoiceListItem[], // AS_JUSTIFIED: Convex query return type polluted by mutations circular-ref error; queries return correct shape
    [rawInvoices]
  );

  const filtered = useMemo(() => {
    let list = invoices;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        inv =>
          inv.invoiceNumber.toLowerCase().includes(q) || inv.companyName?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      list = list.filter(inv => inv.status === statusFilter);
    }
    return list;
  }, [invoices, searchQuery, statusFilter]);

  const invoiceAccessor = useCallback(
    (invoice: IInvoiceListItem, colId: string): string | number => {
      switch (colId) {
        case 'invoiceNumber':
          return invoice.invoiceNumber;
        case 'company':
          return invoice.companyName;
        case 'total':
          return invoice.total;
        case 'status':
          return invoice.status;
        case 'dueDate':
          return invoice.dueDate ?? 0;
        case 'created':
          return invoice.createdAt;
        default:
          return '';
      }
    },
    []
  );
  const {
    sortState,
    toggleSort,
    sortedItems: sortedInvoices,
  } = useTableSort(
    filtered,
    INVOICES_COLUMNS,
    { defaultSort: { sortBy: 'dueDate', direction: 'asc' } },
    invoiceAccessor
  );

  const handleSelectRow = useCallback((invoiceId: Id<'companyInvoices'>, selected: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(invoiceId);
      } else {
        next.delete(invoiceId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedIds(new Set(filtered.map(inv => inv._id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [filtered]
  );

  const handleDeleteConfirm = useCallback(async () => {
    await batchRemove({ invoiceIds: Array.from(selectedIds) });
    setSelectedIds(new Set());
    setIsDeleteOpen(false);
  }, [selectedIds, batchRemove]);

  const isAllSelected = filtered.length > 0 && selectedIds.size === filtered.length;
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage invoices.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Invoices</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? 'Loading...' : `${invoices.length} total`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
              <Trash2 aria-hidden="true" className="w-4 h-4 mr-2" />
              Delete ({selectedIds.size})
            </Button>
          )}
          <Button onClick={() => navigate('/insurance/invoices/new')}>
            <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
            Create Invoice
          </Button>
        </div>
      </div>

      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-table-row-border">
          <label htmlFor="invoice-search" className="sr-only">
            Search invoices
          </label>
          <Input
            id="invoice-search"
            placeholder="Search by invoice # or company..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
          <label htmlFor="invoice-status-filter" className="sr-only">
            Filter by status
          </label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger id="invoice-status-filter" className="w-[180px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="partially_paid">Partially Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto">
            <ColumnSettingsPopover
              allColumns={allColumns}
              isColumnVisible={isColumnVisible}
              toggleColumn={toggleColumn}
              resetToDefaults={resetToDefaults}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          {/* Header */}
          <div className="flex items-center px-6 py-2 border-b border-table-row-border bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-max">
            {/* Checkbox — always first, outside column config */}
            <span className="w-[48px] shrink-0 pr-2">
              <Checkbox
                checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
                onCheckedChange={checked => handleSelectAll(checked === true)}
                aria-label="Select all invoices"
              />
            </span>
            {visibleColumns.map(col => (
              <div
                key={col.id}
                className={cn(
                  col.width ?? 'flex-1',
                  'shrink-0',
                  col.pinned === 'left' && 'sticky left-[48px] bg-muted/30 z-10'
                )}
              >
                <SortableColumnHeader
                  column={col}
                  sortState={sortState}
                  onToggleSort={toggleSort}
                />
              </div>
            ))}
          </div>

          {/* Rows */}
          {isLoading && <InvoiceTableSkeleton />}

          {!isLoading &&
            sortedInvoices.map(invoice => (
              <div
                key={invoice._id}
                className="flex items-center px-6 py-3 border-b border-table-row-border hover:bg-muted/50 cursor-pointer min-w-max"
                onClick={() => navigate(`/insurance/invoices/${invoice._id}`)}
              >
                {/* Checkbox — always first, outside column config */}
                <span
                  className="w-[48px] shrink-0 pr-2"
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selectedIds.has(invoice._id)}
                    onCheckedChange={checked => handleSelectRow(invoice._id, checked === true)}
                    aria-label={`Select invoice ${invoice.invoiceNumber}`}
                  />
                </span>
                {visibleColumns.map(col => (
                  <span
                    key={col.id}
                    className={cn(
                      col.width ?? 'flex-1',
                      'shrink-0',
                      col.pinned === 'left' &&
                        'sticky left-[48px] bg-table-container hover:bg-muted/50 z-10'
                    )}
                  >
                    {renderInvoiceCell(col.id, invoice)}
                  </span>
                ))}
              </div>
            ))}

          {!isLoading && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-1">No invoices found</h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your filters or search query'
                  : 'Create your first invoice to get started'}
              </p>
            </div>
          )}
        </div>
      </div>

      <DeletePoliciesDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        count={selectedIds.size}
      />
    </div>
  );
}
